import { Add, Delete, Edit } from '@mui/icons-material';
import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    MenuItem,
    Select,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import CommonDialog from '../../../common/CommonDialog';
import TermsAndWarrantyModal from '../../../common/TermsAndWarranty/TermsAndWarrantyModal';
import { MuiSelectWithFocus } from '../../../config/MaterialUIConstants';
import { deleteTermsAndWarrenty, setDefaultTermsAndWarrenty } from '../../../redux/Account/ShopDetails/action';
import { coreSettingsKeys } from '../../../services/core/settings';
import SuccessMessageModal from '../../Common/SuccessMessage';

/** Shop details / API may use isInvoiceDefault or legacy isDefaultInvoice */
const termIsInvoiceDefault = (item) => !!(item?.isInvoiceDefault ?? item?.isDefaultInvoice);
const termIsEstimateDefault = (item) => !!(item?.isEstimateDefault ?? item?.isDefaultEstimate);

const ShopTermsAndWarranty = ({ getShopDetails }) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const theme = useTheme();
    useMediaQuery(theme.breakpoints.down('md'));

    const termsAndWarrantyData = useSelector((state) => state?.account?.shopDetailsData?.termsAndWarranty);

    const [openDeleteConfirmModal, setOpenDeleteConfirmModal] = useState(false);
    const [loader, setLoader] = useState(false);
    const [isUpdatingDefaults, setIsUpdatingDefaults] = useState(false);
    const [openTermsModal, setOpenTermsModal] = useState(false);
    const [termsModalMode, setTermsModalMode] = useState('add');
    const [termsModalInitial, setTermsModalInitial] = useState({ title: '', description: '' });
    const [termsData, setTermsData] = useState(null);
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [openSuccessModal, setOpenSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const [defaultInvoiceId, setDefaultInvoiceId] = useState('');
    const [defaultEstimateId, setDefaultEstimateId] = useState('');

    useEffect(() => {
        setTermsData(termsAndWarrantyData);
    }, [termsAndWarrantyData]);

    useEffect(() => {
        const list = Array.isArray(termsAndWarrantyData) ? termsAndWarrantyData : [];
        const invoiceDefault = list.find((i) => termIsInvoiceDefault(i))?._id || '';
        const estimateDefault = list.find((i) => termIsEstimateDefault(i))?._id || '';
        setDefaultInvoiceId(invoiceDefault);
        setDefaultEstimateId(estimateDefault);
    }, [termsAndWarrantyData]);

    const handleOpenAddModal = () => {
        setTermsModalMode('add');
        setTermsModalInitial({ title: '', description: '' });
        setOpenTermsModal(true);
    };

    const handleOpenEditModal = (item) => {
        setTermsModalMode('edit');
        setTermsModalInitial({
            _id: item._id,
            title: item.title,
            description: item.description,
            isInvoiceDefault: termIsInvoiceDefault(item),
            isEstimateDefault: termIsEstimateDefault(item),
        });
        setOpenTermsModal(true);
    };

    const showSuccessMessage = (message) => {
        setOpenSuccessModal(true);
        setSuccessMessage(message);
    };

    const handleModalSuccess = () => {
        getShopDetails();
        // showSuccessMessage('Terms and warranty successfully updated');
    };

    const handleDelete = async () => {
        setLoader(true);
        const result = await dispatch(deleteTermsAndWarrenty(selectedItemId));

        if (result) {
            queryClient.invalidateQueries({
                queryKey: coreSettingsKeys.termsAndWarranty(),
                refetchType: 'all',
            });
            getShopDetails();
            setOpenDeleteConfirmModal(false);
            showSuccessMessage('Changes Saved');
        }

        setLoader(false);
    };

    const openDeleteConfirmation = (itemId) => {
        setSelectedItemId(itemId);
        setOpenDeleteConfirmModal(true);
    };

    const updateDefaultSelection = async (invoiceId, estimateId) => {
        const list = Array.isArray(termsAndWarrantyData) ? termsAndWarrantyData : [];
        if (!list.length) return;

        const inv = invoiceId || '';
        const est = estimateId || '';

        const prevInvoiceId = list.find((i) => termIsInvoiceDefault(i))?._id || '';
        const prevEstimateId = list.find((i) => termIsEstimateDefault(i))?._id || '';

        if (prevInvoiceId === inv && prevEstimateId === est) return;

        setIsUpdatingDefaults(true);
        let allOk = true;
        let lastSuccessMessage = '';
        let didDispatch = false;
        try {
            for (const item of list) {
                const id = item?._id;
                if (!id) continue;
                const nowInv = !!inv && inv === id;
                const nowEst = !!est && est === id;
                const wasInv = termIsInvoiceDefault(item);
                const wasEst = termIsEstimateDefault(item);
                const changed = wasInv !== nowInv || wasEst !== nowEst;
                if (!changed) continue;

                didDispatch = true;
                const result = await dispatch(
                    setDefaultTermsAndWarrenty(id, {
                        isInvoiceDefault: nowInv,
                        isEstimateDefault: nowEst,
                    })
                );
                if (!result?.ok) {
                    allOk = false;
                    toast.error(result?.message || 'Failed to update default terms and warranty.');
                    break;
                }
                if (result?.message) lastSuccessMessage = result.message;
            }

            if (allOk && didDispatch) {
                toast.success(lastSuccessMessage || 'Default terms and warranty updated successfully.');
                queryClient.invalidateQueries({
                    queryKey: coreSettingsKeys.termsAndWarranty(),
                    refetchType: 'all',
                });
                getShopDetails();
            }
        } finally {
            setIsUpdatingDefaults(false);
            if (!allOk) {
                queryClient.invalidateQueries({
                    queryKey: coreSettingsKeys.termsAndWarranty(),
                    refetchType: 'all',
                });
                getShopDetails();
            }
        }
    };

    const TABLE_STYLES = {
        width: '100%',
        p: '12px',
        border: '1px solid rgba(0, 0, 0, 0.10)',
        borderRadius: '8px',
        boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        gap: '8px',
        backgroundColor: '#FFF',
    };

    const TABLE_GRID_STYLES = {
        display: 'grid',
        gridTemplateColumns: `
          1.35fr 12px
          0.8fr
        `,
        alignItems: 'center',
        width: '100%',
        '@media (max-width: 900px)': {
            gridTemplateColumns: '1fr',
            rowGap: '10px',
        },
    };

    const TABLE_HEADER_STYLES = {
        ...TABLE_GRID_STYLES,
        width: '100%',
        minHeight: '38px',
        backgroundColor: '#F8F8F8',
        px: 2,
        fontSize: '14px',
        fontWeight: 600,
        color: '#1F1F1F',
        '@media (max-width: 900px)': {
            display: 'none',
        },
    };

    const TABLE_ROW_STYLES = {
        ...TABLE_GRID_STYLES,
        width: '100%',
        minHeight: '60px',
        px: 2,
        py: 1.5,
    };

    return (
        <Grid container sx={{ boxShadow: 3, bgcolor: 'white' }} justifyContent="space-between" padding="24px" borderRadius="10px" marginTop="32px">
            <Grid item xs={12} sm={4} md={4} lg={3} xl={3}>
                <Typography variant="h5" sx={{ fontWeight: 400, fontSize: '24px' }}>
                    Terms and Warranty
                </Typography>
                <Typography
                    variant="body2"
                    component="div"
                    sx={{
                        mt: '12px',
                        fontSize: '16px',
                        color: '#1F1F1F',
                        lineHeight: '24px',
                        maxWidth: 360,
                    }}
                >
                    Add your own customized warranty and terms specifically designed for your customers on invoices and estimates.
                </Typography>
            </Grid>

            <Grid item xs={12} sm={8} md={8} xl={8} direction="row" spacing={2}>
                <Grid container spacing={2} sx={{ mb: '24px' }}>
                    <Grid item xs={12} md={6}>
                        <Stack gap={'6px'}>
                            <Typography variant="body1" className="label-500">
                                Invoice Default
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    id="invoice-default-select"
                                    value={defaultInvoiceId}
                                    displayEmpty
                                    onChange={async (e) => {
                                        const nextId = e.target.value;
                                        setDefaultInvoiceId(nextId);
                                        await updateDefaultSelection(nextId, defaultEstimateId);
                                    }}
                                    MenuProps={{ style: { maxHeight: '400px' } }}
                                    sx={MuiSelectWithFocus}
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {(termsData || termsAndWarrantyData || []).map((item) => (
                                        <MenuItem key={item?._id} value={item?._id}>
                                            {item?.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Stack gap={'6px'}>
                            <Typography variant="body1" className="label-500">
                                Estimate Default
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    id="estimate-default-select"
                                    value={defaultEstimateId}
                                    displayEmpty
                                    onChange={async (e) => {
                                        const nextId = e.target.value;
                                        setDefaultEstimateId(nextId);
                                        await updateDefaultSelection(defaultInvoiceId, nextId);
                                    }}
                                    MenuProps={{ style: { maxHeight: '400px' } }}
                                    sx={MuiSelectWithFocus}
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {(termsData || termsAndWarrantyData || []).map((item) => (
                                        <MenuItem key={item?._id} value={item?._id}>
                                            {item?.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                    </Grid>
                </Grid>

                <Box sx={TABLE_STYLES}>
                    <Box sx={TABLE_HEADER_STYLES}>
                        <Box>Warranty Title</Box>
                        <Box />
                        <Box sx={{ justifySelf: 'end' }}>Actions</Box>
                    </Box>

                    {(termsData || termsAndWarrantyData || []).map((item) => (
                        <Box sx={TABLE_ROW_STYLES} key={item?._id}>
                            <Typography
                                variant="body1"
                                className="label-500"
                                sx={{
                                    fontSize: '16px',
                                    color: '#1F1F1F',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {item?.title}
                            </Typography>

                            <Box />

                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ justifySelf: 'end' }}>
                                <IconButton
                                    size="small"
                                    aria-label="Edit terms or warranty"
                                    onClick={() => handleOpenEditModal(item)}
                                    sx={{ p: '4px', width: 28, height: 28 }}
                                >
                                    <Edit sx={{ fontSize: 20, color: '#646466' }} />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    aria-label="Delete terms or warranty"
                                    onClick={() => openDeleteConfirmation(item?._id)}
                                    sx={{ p: '4px', width: 28, height: 28 }}
                                >
                                    <Delete sx={{ fontSize: 20, color: '#D32F2F' }} />
                                </IconButton>
                            </Stack>
                        </Box>
                    ))}
                </Box>

                <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={handleOpenAddModal}
                    sx={{
                        width: '100px',
                        height: '38px',
                        padding: '6px 12px',
                        marginTop: '13px',
                        borderRadius: '6px',
                        border: '1px solid #006CDD',
                        background: '#FFF',
                        color: '#006CDD',
                        fontFamily: 'Roboto',
                        fontSize: '15px',
                        fontWeight: 500,
                        lineHeight: '26px',
                        textTransform: 'uppercase',
                        '&:hover': {
                            border: '1px solid #006CDD',
                            background: '#FFF',
                        },
                    }}
                >
                    ADD
                </Button>

                <TermsAndWarrantyModal
                    open={openTermsModal}
                    onClose={() => setOpenTermsModal(false)}
                    mode={termsModalMode}
                    initialValues={termsModalInitial}
                    onSuccess={handleModalSuccess}
                    onRefreshShopDetails={getShopDetails}
                />
            </Grid>

            <CommonDialog
                loader={loader}
                open={openDeleteConfirmModal}
                onClose={() => {
                    if (!loader) {
                        setOpenDeleteConfirmModal(false);
                    }
                }}
                onSave={handleDelete}
                dialogTitle="Delete Confirmation"
                dialogSubtitle="Are you sure you want to delete this term and warranty?"
            />

            <Backdrop open={isUpdatingDefaults} sx={{ color: '#fff', zIndex: (t) => t.zIndex.modal + 1 }}>
                <CircularProgress color="inherit" />
            </Backdrop>

            <SuccessMessageModal message={successMessage} open={openSuccessModal} setOpenSuccessModal={setOpenSuccessModal} />
        </Grid>
    );
};

export default ShopTermsAndWarranty;
